/**
 * PrintIQ KPI top bar API response types.
 * GET /api/piq/kpi returns either success payload or available: false.
 */

/** Day-over-day OTS trend vs the previous snapshot date. Percentage-point deltas, 2 dp; absent on live (non-default) responses. */
export interface PrintIqKpiTrend {
  previous_date: string | null
  TOTAL_OTS_Pct: number | null
  EVT_OTS_Pct: number | null
  LAN_OTS_Pct: number | null
}

export interface PrintIqKpiSuccess {
  TOTAL_Shipped: number
  TOTAL_OnTime: number
  TOTAL_OTS_Pct: number
  EVT_Shipped: number
  EVT_OnTime: number
  EVT_OTS_Pct: number
  LAN_Shipped: number
  LAN_OnTime: number
  LAN_OTS_Pct: number
  /** Shipped dollars (total_sale) in cents — Total / EVT / LAN. Only present when KPI is from live query (OTS page with date range). */
  TOTAL_ShippedSale?: number
  EVT_ShippedSale?: number
  LAN_ShippedSale?: number
  /** Avg lead time (days_to_ship) — Total / EVT / LAN. null when no rows in window; present on the cached snapshot. */
  TOTAL_LeadDays?: number | null
  EVT_LeadDays?: number | null
  LAN_LeadDays?: number | null
  fetched_at: string
  /** Snapshot date range (Y-m-d); when present, OTS page can default to this to match the top bar */
  date_from?: string
  date_to?: string
  /** Day-over-day trend; present only on the default snapshot (nav bar, or OTS page at default range/filters). */
  trend?: PrintIqKpiTrend
}

export interface PrintIqKpiUnavailable {
  available: false
  message: string
  fetched_at?: string
}

export type PrintIqKpiResponse = PrintIqKpiSuccess | PrintIqKpiUnavailable

export function isPrintIqKpiSuccess(
  r: PrintIqKpiResponse
): r is PrintIqKpiSuccess {
  return (
    'TOTAL_Shipped' in r &&
    typeof (r as PrintIqKpiSuccess).TOTAL_Shipped === 'number'
  )
}

/** Internal factory sub-split (EVT or LAN). */
export interface ReportCardInternalFactory {
  scheduled: number
  completed: number
  ots_pct: number | null
}

/** Internal card: by Due Date. Manufactured + Manufactured w/ Hardware. */
export interface ReportCardInternal {
  lines_on_day_by_due_date?: number
  lines_on_day_in_range?: number
  lines_completed_on_day: number
  lines_completed_scheduled_in_range?: number
  ots_pct: number | null
  evt?: ReportCardInternalFactory
  lan?: ReportCardInternalFactory
}

/** External card: by Customer Requested Date. Manufactured + Manufactured w/ Hardware. */
export interface ReportCardExternal {
  lines_on_day_by_customer_requested_date?: number
  lines_on_day_in_range?: number
  lines_completed_on_day: number
  lines_completed_scheduled_in_range?: number
  ots_pct: number | null
}

/** Single-day report card (one row in per_day). */
export interface ReportCardDay {
  date: string
  lines_completed: number
  internal: ReportCardInternal
  external: ReportCardExternal
}

/**
 * Report Card for a single day: Internal + External cards.
 * GET /api/piq/report-card?date=YYYY-MM-DD&factory=both|EVT|LAN
 */
export interface ReportCardSuccess {
  date: string
  lines_completed: number
  internal: ReportCardInternal
  external: ReportCardExternal
  fetched_at: string
}

/**
 * Report Card for a date range: Internal + External aggregates and per_day.
 * GET /api/piq/report-card?date_from=...&date_to=...&factory=...
 */
export interface ReportCardRangeSuccess {
  date_from: string
  date_to: string
  lines_completed_in_range: number
  internal: ReportCardInternal
  external: ReportCardExternal
  per_day: ReportCardDay[]
  fetched_at: string
}

export interface ReportCardUnavailable {
  available: false
  message: string
  fetched_at?: string
}

export type ReportCardResponse =
  | ReportCardSuccess
  | ReportCardRangeSuccess
  | ReportCardUnavailable

export function isReportCardSuccess(
  r: ReportCardResponse
): r is ReportCardSuccess {
  return (
    'internal' in r &&
    'external' in r &&
    'date' in r &&
    !('per_day' in r)
  )
}

export function isReportCardRangeSuccess(
  r: ReportCardResponse
): r is ReportCardRangeSuccess {
  return (
    'internal' in r &&
    'external' in r &&
    'per_day' in r &&
    Array.isArray((r as ReportCardRangeSuccess).per_day)
  )
}

/** One month from GET /api/piq/ots-metrics/monthly (OTS metrics aggregated by month). */
export interface OtsMonthlyMetricRow {
  month: string
  month_label: string
  shipped_total: number
  on_time_count: number
  late_count: number
  on_time_percent: number
  late_percent: number
}

/** One day from GET /api/piq/ots-metrics/daily-lead-time (external cohort avg days to ship by date dispatched). */
export interface OtsDailyLeadTimeRow {
  date: string
  avg_days_to_ship: number | null
  line_count: number
}

export type OtsExternalLeadTimeCohort = 'standard' | 'custom'

/** GET /api/piq/ots-metrics/daily-lead-time — combined + EVT + LAN series. */
export interface OtsDailyLeadTimeBreakdown {
  cohort: OtsExternalLeadTimeCohort
  target_days: number
  combined: OtsDailyLeadTimeRow[]
  evt: OtsDailyLeadTimeRow[]
  lan: OtsDailyLeadTimeRow[]
}

/** One week from GET /api/piq/ots-metrics/weekly-lead-time (Monday→Sunday, newest week first). */
export interface OtsWeeklyLeadTimeRow {
  week_start: string
  week_end: string
  avg_days_to_ship: number | null
  line_count: number
}

/** GET /api/piq/ots-metrics/weekly-lead-time — combined + EVT + LAN weekly series. */
export interface OtsWeeklyLeadTimeBreakdown {
  cohort: string
  target_days: number
  weeks: number
  date_from: string
  date_to: string
  combined: OtsWeeklyLeadTimeRow[]
  evt: OtsWeeklyLeadTimeRow[]
  lan: OtsWeeklyLeadTimeRow[]
}

/** GET /api/piq/ots-metrics/job-type-shipped-sales — row when factory is both. */
export interface OtsJobTypeShippedSalesRowBoth {
  job_type: string
  all_cents: number
  evt_cents: number
  lan_cents: number
  total_cents?: number
}

/** Row when factory is EVT or LAN (filtered). */
export interface OtsJobTypeShippedSalesRowSingle {
  job_type: string
  total_cents: number
  all_cents?: number
  evt_cents?: number
  lan_cents?: number
}

export type OtsJobTypeShippedSalesResponse =
  | {
      factory_mode: 'both'
      rows: OtsJobTypeShippedSalesRowBoth[]
      grand_total: { all_cents: number; evt_cents: number; lan_cents: number }
    }
  | {
      factory_mode: 'EVT' | 'LAN'
      rows: OtsJobTypeShippedSalesRowSingle[]
      grand_total: { total_cents: number }
    }
